import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, Plus, Edit2, Trash2, Calendar, Users, 
  History, Tag, Check, X, Eye, Package, RotateCcw, ClipboardList
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Price input card component for each egg size (with cracks) - moved outside to prevent re-renders
const PriceCard = ({ title, color, crateKey, pieceKey, crackCrateKey, crackPieceKey, formData, setFormData, currencySymbol }) => (
  <div className={`rounded-lg border-2 ${color} p-4`} data-testid={`price-card-${title.toLowerCase().replace(' ', '-')}`}>
    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
      <Package className="w-4 h-4" />
      {title}
    </h4>
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-slate-600">Crate (30 eggs)</Label>
          <div className="relative mt-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{currencySymbol}</span>
            <Input
              type="number"
              step="0.01"
              value={formData[crateKey]}
              onChange={(e) => setFormData(prev => ({ ...prev, [crateKey]: e.target.value }))}
              className="pl-6 text-sm"
              placeholder="0.00"
              data-testid={`input-${crateKey}`}
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Per Piece</Label>
          <div className="relative mt-1">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{currencySymbol}</span>
            <Input
              type="number"
              step="0.01"
              value={formData[pieceKey]}
              onChange={(e) => setFormData(prev => ({ ...prev, [pieceKey]: e.target.value }))}
              className="pl-6 text-sm"
              placeholder="0.00"
              data-testid={`input-${pieceKey}`}
            />
          </div>
        </div>
      </div>
      {/* Crack prices */}
      <div className="pt-2 border-t border-red-200">
        <p className="text-xs font-medium text-red-600 mb-2">Cracked {title}</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-slate-500">Crack Crate</Label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{currencySymbol}</span>
              <Input
                type="number"
                step="0.01"
                value={formData[crackCrateKey]}
                onChange={(e) => setFormData(prev => ({ ...prev, [crackCrateKey]: e.target.value }))}
                className="pl-6 text-sm bg-red-50/50"
                placeholder="0.00"
                data-testid={`input-${crackCrateKey}`}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Crack Piece</Label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{currencySymbol}</span>
              <Input
                type="number"
                step="0.01"
                value={formData[crackPieceKey]}
                onChange={(e) => setFormData(prev => ({ ...prev, [crackPieceKey]: e.target.value }))}
                className="pl-6 text-sm bg-red-50/50"
                placeholder="0.00"
                data-testid={`input-${crackPieceKey}`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Simpler price card for Damaged (no cracks section) - moved outside
const DamagedPriceCard = ({ title, color, crateKey, pieceKey, formData, setFormData, currencySymbol }) => (
  <div className={`rounded-lg border-2 ${color} p-4`} data-testid={`price-card-${title.toLowerCase()}`}>
    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
      <Package className="w-4 h-4" />
      {title}
    </h4>
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-slate-600">Per Crate (30 eggs)</Label>
        <div className="relative mt-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">{currencySymbol}</span>
          <Input
            type="number"
            step="0.01"
            value={formData[crateKey]}
            onChange={(e) => setFormData(prev => ({ ...prev, [crateKey]: e.target.value }))}
            className="pl-8"
            placeholder="0.00"
            data-testid={`input-${crateKey}`}
          />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600">Per Piece</Label>
        <div className="relative mt-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">{currencySymbol}</span>
          <Input
            type="number"
            step="0.01"
            value={formData[pieceKey]}
            onChange={(e) => setFormData(prev => ({ ...prev, [pieceKey]: e.target.value }))}
            className="pl-8"
            placeholder="0.00"
            data-testid={`input-${pieceKey}`}
          />
        </div>
      </div>
    </div>
  </div>
);

// Current price display card (with optional crack prices) - moved outside
const CurrentPriceCard = ({ title, crate, piece, crackCrate, crackPiece, bgColor, textColor, currencySymbol, formatCurrency }) => (
  <div className={`rounded-lg p-3 border ${bgColor}`}>
    <p className="text-xs text-slate-500 mb-1">{title}</p>
    <p className={`font-semibold ${textColor}`}>{currencySymbol}{formatCurrency(crate)}/crate</p>
    <p className="text-sm text-slate-600">{currencySymbol}{formatCurrency(piece)}/pc</p>
    {(crackCrate !== undefined || crackPiece !== undefined) && (
      <div className="mt-1 pt-1 border-t border-red-100">
        <p className="text-xs text-red-500">Cracked:</p>
        <p className="text-xs text-red-600">{currencySymbol}{formatCurrency(crackCrate)}/crt, {currencySymbol}{formatCurrency(crackPiece)}/pc</p>
      </div>
    )}
  </div>
);

const PriceManagement = () => {
  const [schedules, setSchedules] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [currentPrices, setCurrentPrices] = useState(null);
  const [activeTab, setActiveTab] = useState('schedule');
  const [auditLogs, setAuditLogs] = useState([]);
  const [frozenVariants, setFrozenVariants] = useState([]);
  const [frozenPrices, setFrozenPrices] = useState({}); // {variant_key: string}
  const [emptyBagPrice, setEmptyBagPrice] = useState('1300'); // ₦ per dozen
  
  // Get currency symbol from localStorage
  const currencySymbol = localStorage.getItem('currency_symbol') || '₦';
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser?.role === 'admin';
  
  const initialFormData = {
    effective_date: new Date().toISOString().split('T')[0],
    end_date: '',
    apply_to: 'all',
    // Extra Large prices
    extra_large_crate: '',
    extra_large_piece: '',
    extra_large_crack_crate: '',
    extra_large_crack_piece: '',
    // Large prices
    large_crate: '',
    large_piece: '',
    large_crack_crate: '',
    large_crack_piece: '',
    // Medium prices
    medium_crate: '',
    medium_piece: '',
    medium_crack_crate: '',
    medium_crack_piece: '',
    // Small prices
    small_crate: '',
    small_piece: '',
    small_crack_crate: '',
    small_crack_piece: '',
    // Damaged prices (no cracks)
    damaged_crate: '',
    damaged_piece: '',
    notes: ''
  };
  
  const [formData, setFormData] = useState(initialFormData);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchSchedules();
    fetchCustomers();
    fetchCurrentPrices();
    fetchFrozenVariants();
  }, []);

  const fetchFrozenVariants = async () => {
    try {
      const response = await axios.get(`${API}/frozen-chicken/variants`, getAuthHeader());
      setFrozenVariants(response.data || []);
      // Seed empty frozen prices with variant defaults (only when not editing)
      setFrozenPrices(prev => {
        if (Object.keys(prev).length) return prev;
        const seed = {};
        (response.data || []).forEach(v => { seed[v.key] = (v.default_price ?? '').toString(); });
        return seed;
      });
    } catch (error) {
      console.error('Failed to fetch frozen variants:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'audit') fetchAuditLogs();
  }, [activeTab]);

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/price-schedules/audit`, getAuthHeader());
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch price audit log:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/price-schedules/history`, getAuthHeader());
      setSchedules(response.data);
    } catch (error) {
      console.error('Failed to fetch price schedules:', error);
      toast.error('Failed to load price schedules');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPrices = async () => {
    try {
      const response = await axios.get(`${API}/price-schedules/current`, getAuthHeader());
      setCurrentPrices(response.data);
    } catch (error) {
      console.error('Failed to fetch current prices:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      // Fetch from Customers tab (not users with customer role)
      const response = await axios.get(`${API}/customers`, getAuthHeader());
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingSchedule(null);
    const seed = {};
    frozenVariants.forEach(v => { seed[v.key] = (v.default_price ?? '').toString(); });
    setFrozenPrices(seed);
    setEmptyBagPrice('1300');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.end_date && formData.end_date < formData.effective_date) {
      toast.error('End date cannot be before the effective date');
      return;
    }
    
    const payload = {
      ...formData,
      end_date: formData.end_date || null,
      // Extra Large
      extra_large_crate: parseFloat(formData.extra_large_crate) || 0,
      extra_large_piece: parseFloat(formData.extra_large_piece) || 0,
      extra_large_crack_crate: parseFloat(formData.extra_large_crack_crate) || 0,
      extra_large_crack_piece: parseFloat(formData.extra_large_crack_piece) || 0,
      // Large
      large_crate: parseFloat(formData.large_crate) || 0,
      large_piece: parseFloat(formData.large_piece) || 0,
      large_crack_crate: parseFloat(formData.large_crack_crate) || 0,
      large_crack_piece: parseFloat(formData.large_crack_piece) || 0,
      // Medium
      medium_crate: parseFloat(formData.medium_crate) || 0,
      medium_piece: parseFloat(formData.medium_piece) || 0,
      medium_crack_crate: parseFloat(formData.medium_crack_crate) || 0,
      medium_crack_piece: parseFloat(formData.medium_crack_piece) || 0,
      // Small
      small_crate: parseFloat(formData.small_crate) || 0,
      small_piece: parseFloat(formData.small_piece) || 0,
      small_crack_crate: parseFloat(formData.small_crack_crate) || 0,
      small_crack_piece: parseFloat(formData.small_crack_piece) || 0,
      // Damaged
      damaged_crate: parseFloat(formData.damaged_crate) || 0,
      damaged_piece: parseFloat(formData.damaged_piece) || 0,
      // Frozen chicken (per pack variant)
      frozen_chicken_prices: frozenVariants.reduce((acc, v) => {
        acc[v.key] = parseFloat(frozenPrices[v.key]) || 0;
        return acc;
      }, {}),
      // Empty chicken feed bags (per dozen)
      empty_feed_bag_price: emptyBagPrice === '' ? null : (parseFloat(emptyBagPrice) || 0)
    };

    try {
      // Guardrail: warn when this schedule would override the current active prices
      const currentActiveDate = currentPrices?.id ? currentPrices.effective_date : null;
      const isEditingActive = editingSchedule && editingSchedule.id === currentPrices?.id;
      if (payload.apply_to === 'all' && currentActiveDate && payload.effective_date >= currentActiveDate && !isEditingActive) {
        const proceed = window.confirm(
          `Heads up: this price schedule (effective ${formatDate(payload.effective_date)}) will OVERRIDE the current active prices ` +
          `(effective ${formatDate(currentActiveDate)}) from ${formatDate(payload.effective_date)} onward.\n\nDo you want to continue?`
        );
        if (!proceed) return;
      }

      if (editingSchedule) {
        await axios.put(`${API}/price-schedules/${editingSchedule.id}`, payload, getAuthHeader());
        toast.success('Price schedule updated successfully');
      } else {
        await axios.post(`${API}/price-schedules`, payload, getAuthHeader());
        toast.success('Price schedule created successfully');
      }
      resetForm();
      fetchSchedules();
      fetchCurrentPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save price schedule');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      effective_date: schedule.effective_date,
      end_date: schedule.end_date || '',
      apply_to: schedule.apply_to,
      // Extra Large
      extra_large_crate: schedule.extra_large_crate?.toString() || '',
      extra_large_piece: schedule.extra_large_piece?.toString() || '',
      extra_large_crack_crate: schedule.extra_large_crack_crate?.toString() || '',
      extra_large_crack_piece: schedule.extra_large_crack_piece?.toString() || '',
      // Large
      large_crate: schedule.large_crate?.toString() || '',
      large_piece: schedule.large_piece?.toString() || '',
      large_crack_crate: schedule.large_crack_crate?.toString() || '',
      large_crack_piece: schedule.large_crack_piece?.toString() || '',
      // Medium
      medium_crate: schedule.medium_crate?.toString() || '',
      medium_piece: schedule.medium_piece?.toString() || '',
      medium_crack_crate: schedule.medium_crack_crate?.toString() || '',
      medium_crack_piece: schedule.medium_crack_piece?.toString() || '',
      // Small
      small_crate: schedule.small_crate?.toString() || '',
      small_piece: schedule.small_piece?.toString() || '',
      small_crack_crate: schedule.small_crack_crate?.toString() || '',
      small_crack_piece: schedule.small_crack_piece?.toString() || '',
      // Damaged
      damaged_crate: schedule.damaged_crate?.toString() || '',
      damaged_piece: schedule.damaged_piece?.toString() || '',
      notes: schedule.notes || ''
    });
    // Populate frozen chicken prices (schedule value, else variant default)
    const fp = {};
    frozenVariants.forEach(v => {
      const val = schedule.frozen_chicken_prices?.[v.key];
      fp[v.key] = (val !== undefined && val !== null ? val : (v.default_price ?? '')).toString();
    });
    setFrozenPrices(fp);
    setEmptyBagPrice((schedule.empty_feed_bag_price ?? 1300).toString());
    // Switch to schedule tab and scroll to form
    setActiveTab('schedule');
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this price schedule?')) return;
    
    try {
      await axios.delete(`${API}/price-schedules/${scheduleId}`, getAuthHeader());
      toast.success('Price schedule deleted');
      fetchSchedules();
      fetchCurrentPrices();
    } catch (error) {
      toast.error('Failed to delete price schedule');
    }
  };

  const handleRestoreToDate = async (schedule) => {
    const dateLabel = formatDate(schedule.effective_date);
    if (!window.confirm(
      `Restore prices to the schedule effective ${dateLabel}?\n\n` +
      `This will deactivate ALL price schedules dated after ${dateLabel}, so these prices apply going forward until an Admin changes them.`
    )) return;
    try {
      const response = await axios.post(
        `${API}/price-schedules/restore-to-date`,
        { date: schedule.effective_date },
        getAuthHeader()
      );
      toast.success(response.data.message);
      fetchSchedules();
      fetchCurrentPrices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore prices');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { minimumFractionDigits: 2 }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="price-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Tag className="w-7 h-7 text-emerald-600" />
            Price Management
          </h1>
          <p className="text-slate-500 mt-1">Manage egg prices for all customers or specific customers</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="schedule" className="flex items-center gap-2" data-testid="tab-schedule">
            <DollarSign className="w-4 h-4" />
            Price Schedule
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
            <History className="w-4 h-4" />
            Price History
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2" data-testid="tab-audit">
            <ClipboardList className="w-4 h-4" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        {/* Price Schedule Tab */}
        <TabsContent value="schedule" className="space-y-6">
          {/* Current Prices Card */}
          {currentPrices && (
            <div className="card p-6 bg-gradient-to-r from-emerald-50 to-white" data-testid="current-prices-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg text-slate-800 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-emerald-600" />
                  Current Active Prices
                </h3>
                <span className="text-sm text-slate-500">
                  Effective: {formatDate(currentPrices.effective_date)}
                  {currentPrices.end_date && <span className="ml-1 text-amber-600">(until {formatDate(currentPrices.end_date)})</span>}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                <CurrentPriceCard 
                  title="Extra Large" 
                  crate={currentPrices.extra_large_crate} 
                  piece={currentPrices.extra_large_piece}
                  crackCrate={currentPrices.extra_large_crack_crate}
                  crackPiece={currentPrices.extra_large_crack_piece}
                  bgColor="bg-white border-purple-200"
                  textColor="text-purple-700"
                  currencySymbol={currencySymbol}
                  formatCurrency={formatCurrency}
                />
                <CurrentPriceCard 
                  title="Large" 
                  crate={currentPrices.large_crate} 
                  piece={currentPrices.large_piece}
                  crackCrate={currentPrices.large_crack_crate}
                  crackPiece={currentPrices.large_crack_piece}
                  bgColor="bg-white border-emerald-200"
                  textColor="text-emerald-700"
                  currencySymbol={currencySymbol}
                  formatCurrency={formatCurrency}
                />
                <CurrentPriceCard 
                  title="Medium" 
                  crate={currentPrices.medium_crate} 
                  piece={currentPrices.medium_piece}
                  crackCrate={currentPrices.medium_crack_crate}
                  crackPiece={currentPrices.medium_crack_piece}
                  bgColor="bg-white border-blue-200"
                  textColor="text-blue-700"
                  currencySymbol={currencySymbol}
                  formatCurrency={formatCurrency}
                />
                <CurrentPriceCard 
                  title="Small" 
                  crate={currentPrices.small_crate} 
                  piece={currentPrices.small_piece}
                  crackCrate={currentPrices.small_crack_crate}
                  crackPiece={currentPrices.small_crack_piece}
                  bgColor="bg-white border-amber-200"
                  textColor="text-amber-700"
                  currencySymbol={currencySymbol}
                  formatCurrency={formatCurrency}
                />
                <CurrentPriceCard 
                  title="Damaged" 
                  crate={currentPrices.damaged_crate} 
                  piece={currentPrices.damaged_piece}
                  bgColor="bg-white border-orange-200"
                  textColor="text-orange-700"
                  currencySymbol={currencySymbol}
                  formatCurrency={formatCurrency}
                />
              </div>
              {currentPrices.frozen_chicken_prices && Object.keys(currentPrices.frozen_chicken_prices).length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Frozen Chicken (per pack)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {frozenVariants.map(v => (
                      <div key={v.key} className="bg-white border border-cyan-200 rounded-xl p-3" data-testid={`current-frozen-${v.key}`}>
                        <p className="text-xs font-medium text-cyan-700">{v.label || `${v.key} Pack`}</p>
                        <p className="text-lg font-bold text-slate-800 mt-1">{currencySymbol}{formatCurrency(currentPrices.frozen_chicken_prices[v.key] || 0)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {currentPrices.empty_feed_bag_price !== undefined && currentPrices.empty_feed_bag_price !== null && (
                <div className="mt-4">
                  <p className="text-sm font-semibold text-slate-700 mb-2">Empty Chicken Feed Bags (per dozen)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    <div className="bg-white border border-amber-200 rounded-xl p-3" data-testid="current-empty-feed-bag-price">
                      <p className="text-xs font-medium text-amber-700">Empty Feed Bags</p>
                      <p className="text-lg font-bold text-slate-800 mt-1">{currencySymbol}{formatCurrency(currentPrices.empty_feed_bag_price || 0)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* New/Edit Price Change Card */}
          <div className="card p-6" data-testid="price-form-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-lg text-slate-800 flex items-center gap-2">
                  {editingSchedule ? <Edit2 className="w-5 h-5 text-blue-600" /> : <Plus className="w-5 h-5 text-emerald-600" />}
                  {editingSchedule ? 'Edit Price Schedule' : 'New Price Change'}
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  {editingSchedule 
                    ? 'Update the price schedule details below' 
                    : 'Schedule a price change for all or a specific customer. Set an optional end date, insert a mid-period change at any time, or apply a price for a single day only.'}
                </p>
              </div>
              {editingSchedule && (
                <Button variant="outline" size="sm" onClick={resetForm} data-testid="btn-cancel-edit">
                  <X className="w-4 h-4 mr-1" /> Cancel Edit
                </Button>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Date and Apply To */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    Effective Date *
                  </Label>
                  <Input
                    type="date"
                    value={formData.effective_date}
                    onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="input-effective-date"
                  />
                  <p className="text-xs text-slate-400 mt-1">Date the price starts to apply (can be a past, present or future date).</p>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    End Date (Optional)
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="date"
                      value={formData.end_date}
                      min={formData.effective_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="flex-1"
                      data-testid="input-end-date"
                    />
                    {formData.end_date && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, end_date: '' })}
                        className="text-slate-400 hover:text-slate-600 px-2"
                        data-testid="btn-clear-end-date"
                        title="Clear end date (applies indefinitely)"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, end_date: formData.effective_date })}
                      className="text-xs text-emerald-600 hover:text-emerald-700 underline"
                      data-testid="btn-single-day"
                    >
                      Apply for a single day only
                    </button>
                    <span className="text-xs text-slate-400">Leave empty to apply until superseded.</span>
                  </div>
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" />
                    Apply To *
                  </Label>
                  <Select 
                    value={formData.apply_to} 
                    onValueChange={(v) => setFormData({ ...formData, apply_to: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="select-apply-to">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          All Customers
                        </div>
                      </SelectItem>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Price Cards for Each Egg Size */}
              <div>
                <Label className="text-base font-semibold text-slate-700 mb-3 block">
                  Set Prices by Egg Size
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  <PriceCard 
                    title="Extra Large" 
                    color="border-purple-200 bg-purple-50/50" 
                    crateKey="extra_large_crate" 
                    pieceKey="extra_large_piece"
                    crackCrateKey="extra_large_crack_crate"
                    crackPieceKey="extra_large_crack_piece"
                    formData={formData}
                    setFormData={setFormData}
                    currencySymbol={currencySymbol}
                  />
                  <PriceCard 
                    title="Large" 
                    color="border-emerald-200 bg-emerald-50/50" 
                    crateKey="large_crate" 
                    pieceKey="large_piece"
                    crackCrateKey="large_crack_crate"
                    crackPieceKey="large_crack_piece"
                    formData={formData}
                    setFormData={setFormData}
                    currencySymbol={currencySymbol}
                  />
                  <PriceCard 
                    title="Medium" 
                    color="border-blue-200 bg-blue-50/50" 
                    crateKey="medium_crate" 
                    pieceKey="medium_piece"
                    crackCrateKey="medium_crack_crate"
                    crackPieceKey="medium_crack_piece"
                    formData={formData}
                    setFormData={setFormData}
                    currencySymbol={currencySymbol}
                  />
                  <PriceCard 
                    title="Small" 
                    color="border-amber-200 bg-amber-50/50" 
                    crateKey="small_crate" 
                    pieceKey="small_piece"
                    crackCrateKey="small_crack_crate"
                    crackPieceKey="small_crack_piece"
                    formData={formData}
                    setFormData={setFormData}
                    currencySymbol={currencySymbol}
                  />
                  <DamagedPriceCard 
                    title="Damaged" 
                    color="border-orange-200 bg-orange-50/50" 
                    crateKey="damaged_crate" 
                    pieceKey="damaged_piece"
                    formData={formData}
                    setFormData={setFormData}
                    currencySymbol={currencySymbol}
                  />
                </div>
              </div>

              {/* Frozen Chicken Prices */}
              <div>
                <Label className="text-base font-semibold text-slate-700 mb-3 block">
                  Set Frozen Chicken Prices (per pack)
                </Label>
                {frozenVariants.length === 0 ? (
                  <p className="text-sm text-slate-400">No frozen chicken pack variants configured.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {frozenVariants.map(v => (
                      <div key={v.key} className="rounded-xl border border-cyan-200 bg-cyan-50/50 p-3">
                        <p className="text-sm font-semibold text-slate-700 mb-2">{v.label || `${v.key} Pack`}</p>
                        <Label className="text-xs text-slate-500">Price / pack</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currencySymbol}</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="pl-6"
                            value={frozenPrices[v.key] ?? ''}
                            onChange={(e) => setFrozenPrices(prev => ({ ...prev, [v.key]: e.target.value }))}
                            data-testid={`input-frozen-${v.key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Empty Chicken Feed Bags Price */}
              <div>
                <Label className="text-base font-semibold text-slate-700 mb-3 block">
                  Set Empty Chicken Feed Bags Price (per dozen)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                    <p className="text-sm font-semibold text-slate-700 mb-2">Empty Feed Bags</p>
                    <Label className="text-xs text-slate-500">Price / dozen</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currencySymbol}</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="pl-6"
                        value={emptyBagPrice}
                        onChange={(e) => setEmptyBagPrice(e.target.value)}
                        data-testid="input-empty-feed-bag-price"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Reason for price change..."
                  rows={2}
                  className="mt-1"
                  data-testid="input-notes"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={resetForm}
                  data-testid="btn-reset"
                >
                  Reset
                </Button>
                <Button 
                  type="submit" 
                  className={editingSchedule ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"}
                  data-testid="btn-submit"
                >
                  {editingSchedule ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Update Price Schedule
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Price Change
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </TabsContent>

        {/* Price History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="card" data-testid="price-history-card">
            <div className="p-4 border-b bg-slate-50">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <History className="w-5 h-5 text-slate-600" />
                Price Change History
              </h3>
            </div>
            
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : schedules.length === 0 ? (
              <div className="p-12 text-center">
                <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No Price History</h3>
                <p className="text-slate-500 mt-2">Create your first price schedule above.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="history-table">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="p-3 font-semibold text-slate-700">Effective Date</th>
                      <th className="p-3 font-semibold text-slate-700">End Date</th>
                      <th className="p-3 font-semibold text-slate-700">Apply To</th>
                      <th className="p-3 font-semibold text-slate-700 text-center">Extra Large</th>
                      <th className="p-3 font-semibold text-slate-700 text-center">Large</th>
                      <th className="p-3 font-semibold text-slate-700 text-center">Medium</th>
                      <th className="p-3 font-semibold text-slate-700 text-center">Small</th>
                      <th className="p-3 font-semibold text-slate-700 text-center">Damaged</th>
                      <th className="p-3 font-semibold text-slate-700">Created</th>
                      <th className="p-3 font-semibold text-slate-700">Status</th>
                      <th className="p-3 font-semibold text-slate-700 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {schedules.map((schedule) => (
                      <tr key={schedule.id} className={`hover:bg-slate-50 ${!schedule.is_active ? 'opacity-50' : ''}`}>
                        <td className="p-3">
                          <span className="font-medium">{formatDate(schedule.effective_date)}</span>
                        </td>
                        <td className="p-3">
                          {schedule.end_date ? (
                            schedule.end_date === schedule.effective_date ? (
                              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Single day</span>
                            ) : (
                              <span className="text-slate-700 text-sm">{formatDate(schedule.end_date)}</span>
                            )
                          ) : (
                            <span className="text-slate-400 text-xs">Until superseded</span>
                          )}
                        </td>
                        <td className="p-3">
                          {schedule.apply_to === 'all' ? (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                              All Customers
                            </span>
                          ) : (
                            <span className="text-slate-700">{schedule.customer_name || 'Specific Customer'}</span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <div className="text-sm">
                            <span className="font-medium text-purple-700">{currencySymbol}{formatCurrency(schedule.extra_large_crate)}</span>
                            <span className="text-slate-400 text-xs block">{currencySymbol}{formatCurrency(schedule.extra_large_piece)}/pc</span>
                            {(schedule.extra_large_crack_crate > 0 || schedule.extra_large_crack_piece > 0) && (
                              <span className="text-red-500 text-xs block">Crk: {currencySymbol}{formatCurrency(schedule.extra_large_crack_crate)}/{currencySymbol}{formatCurrency(schedule.extra_large_crack_piece)}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="text-sm">
                            <span className="font-medium text-emerald-700">{currencySymbol}{formatCurrency(schedule.large_crate)}</span>
                            <span className="text-slate-400 text-xs block">{currencySymbol}{formatCurrency(schedule.large_piece)}/pc</span>
                            {(schedule.large_crack_crate > 0 || schedule.large_crack_piece > 0) && (
                              <span className="text-red-500 text-xs block">Crk: {currencySymbol}{formatCurrency(schedule.large_crack_crate)}/{currencySymbol}{formatCurrency(schedule.large_crack_piece)}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="text-sm">
                            <span className="font-medium text-blue-700">{currencySymbol}{formatCurrency(schedule.medium_crate)}</span>
                            <span className="text-slate-400 text-xs block">{currencySymbol}{formatCurrency(schedule.medium_piece)}/pc</span>
                            {(schedule.medium_crack_crate > 0 || schedule.medium_crack_piece > 0) && (
                              <span className="text-red-500 text-xs block">Crk: {currencySymbol}{formatCurrency(schedule.medium_crack_crate)}/{currencySymbol}{formatCurrency(schedule.medium_crack_piece)}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="text-sm">
                            <span className="font-medium text-amber-700">{currencySymbol}{formatCurrency(schedule.small_crate)}</span>
                            <span className="text-slate-400 text-xs block">{currencySymbol}{formatCurrency(schedule.small_piece)}/pc</span>
                            {(schedule.small_crack_crate > 0 || schedule.small_crack_piece > 0) && (
                              <span className="text-red-500 text-xs block">Crk: {currencySymbol}{formatCurrency(schedule.small_crack_crate)}/{currencySymbol}{formatCurrency(schedule.small_crack_piece)}</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="text-sm">
                            <span className="font-medium text-orange-700">{currencySymbol}{formatCurrency(schedule.damaged_crate)}</span>
                            <span className="text-slate-400 text-xs block">{currencySymbol}{formatCurrency(schedule.damaged_piece)}/pc</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-sm">
                            <span className="text-slate-700">{schedule.created_by_name}</span>
                            <span className="text-slate-400 text-xs block">{formatDateTime(schedule.created_at)}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          {schedule.is_active ? (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                              <Check className="w-3 h-3" /> Active
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                              <X className="w-3 h-3" /> Deleted
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {schedule.is_active && (
                            <div className="flex justify-end gap-1">
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRestoreToDate(schedule)}
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  title="Restore these prices going forward (deactivate any later schedules)"
                                  data-testid={`btn-restore-${schedule.id}`}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEdit(schedule)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`btn-edit-${schedule.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(schedule.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`btn-delete-${schedule.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Notes Section */}
          {schedules.some(s => s.notes) && (
            <div className="card p-4" data-testid="price-notes-section">
              <h4 className="font-semibold text-slate-800 mb-3">Price Change Notes</h4>
              <div className="space-y-2">
                {schedules.filter(s => s.notes).map((schedule) => (
                  <div key={schedule.id} className="p-3 bg-slate-50 rounded-lg text-sm">
                    <span className="font-medium">{formatDate(schedule.effective_date)}:</span>
                    <span className="text-slate-600 ml-2">{schedule.notes}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="space-y-4">
          <div className="card overflow-hidden">
            <div className="flex items-center gap-2 px-4 md:px-6 py-4 border-b">
              <ClipboardList className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-800">Price Change Audit Trail</h3>
            </div>
            <div className="max-h-[60vh] overflow-y-auto" data-testid="price-audit-list">
              {auditLogs.length === 0 ? (
                <p className="text-center text-slate-500 py-10">No price changes recorded yet.</p>
              ) : (
                <div className="divide-y">
                  {auditLogs.map((log, idx) => {
                    const colors = {
                      created: 'bg-emerald-100 text-emerald-700',
                      edited: 'bg-blue-100 text-blue-700',
                      deleted: 'bg-red-100 text-red-700',
                      restored: 'bg-amber-100 text-amber-700'
                    };
                    return (
                      <div key={log.id || idx} className="flex items-start justify-between gap-3 px-4 md:px-6 py-3" data-testid="price-audit-entry">
                        <div className="flex items-start gap-3">
                          <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 rounded-full ${colors[log.action] || 'bg-slate-100 text-slate-600'}`}>
                            {log.action}
                          </span>
                          <div>
                            <p className="text-sm text-slate-700">{log.details}</p>
                            <p className="text-xs text-slate-400 mt-0.5">by {log.changed_by_name}</p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {log.changed_at ? new Date(log.changed_at).toLocaleString() : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PriceManagement;
